import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  AdvancedMarker,
  Pin,
  useAdvancedMarkerRef,
  useMap,
} from "@vis.gl/react-google-maps";
import { useAppSelector, useReverseGeocoding } from "hooks";
import { mapMarkerImage } from "assets";
import { RootState } from "store";
import {
  CURRENT_LOCATION,
  DEFAULT_POLYLINEWEIGHT,
  START_LOCATION,
} from "../../../constants";
import LocationInfoWindow from "./LocationInfoWindow";
import { PolylineWithCarMarkersProps } from "type";

const PolylineWithCarMarkers: React.FC<PolylineWithCarMarkersProps> = ({
  points,
  polylineWeight = DEFAULT_POLYLINEWEIGHT,
  startMarkerTitle = START_LOCATION,
  endMarkerTitle = CURRENT_LOCATION,
}) => {
  // Return early if no points at all
  if (!points || points.length === 0) {
    return null;
  }

  const map = useMap();
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const coordinates = useAppSelector(
    (state: RootState) => state?.coordinates?.coordinates
  );

  // State for InfoWindows - end marker InfoWindow is open by default
  const [startInfoWindowOpen, setStartInfoWindowOpen] = useState(false);
  const [endInfoWindowOpen, setEndInfoWindowOpen] = useState(true);
  const [startMarkerRef, startMarker] = useAdvancedMarkerRef();
  const [endMarkerRef, endMarker] = useAdvancedMarkerRef();

  // Use the custom hooks for geocoding
  const startGeocoding = useReverseGeocoding();
  const endGeocoding = useReverseGeocoding();

  // Helper function to check if two points are the same
  const arePointsEqual = useCallback(
    (
      point1: google.maps.LatLngLiteral,
      point2: google.maps.LatLngLiteral,
      tolerance = 0.00001
    ) => {
      return (
        Math.abs(point1.lat - point2.lat) < tolerance &&
        Math.abs(point1.lng - point2.lng) < tolerance
      );
    },
    []
  );

  // Helper function to check if all points are the same
  const areAllPointsSame = useCallback(
    (points: google.maps.LatLngLiteral[]) => {
      if (points.length <= 1) return true;

      const firstPoint = points[0];
      return points.every((point) => arePointsEqual(firstPoint, point));
    },
    [arePointsEqual]
  );

  // Memoized positions - handle single point case and same points case
  const { startPosition, currentEndPosition, shouldShowStartMarker } =
    useMemo(() => {
      if (!points || points.length === 0) {
        return {
          startPosition: null,
          currentEndPosition: null,
          shouldShowStartMarker: false,
        };
      }

      if (points.length === 1) {
        // For single point, show only current location marker
        return {
          startPosition: null,
          currentEndPosition: points[0],
          shouldShowStartMarker: false,
        };
      }

      // Check if all points are the same
      const allSame = areAllPointsSame(points);

      if (allSame) {
        // If all points are the same, show only current location marker
        return {
          startPosition: points[0],
          currentEndPosition: points[points.length - 1],
          shouldShowStartMarker: false,
        };
      }

      // For multiple different points, show both start and end markers
      return {
        startPosition: points[0],
        currentEndPosition: points[points.length - 1],
        shouldShowStartMarker: true,
      };
    }, [points, areAllPointsSame]);

  // Memoized polyline color
  const polylineColor = useMemo(() => {
    return getComputedStyle(document.documentElement)
      .getPropertyValue("--polyline-color")
      .trim();
  }, []);

  // Return early if we don't have any valid position
  if (!currentEndPosition) {
    return null;
  }

  // Handle start marker click - toggle InfoWindow
  const handleStartMarkerClick = useCallback(async () => {
    const newState = !startInfoWindowOpen;
    setStartInfoWindowOpen(newState);

    // Only fetch location if opening and not already loaded
    if (
      newState &&
      !startGeocoding.locationName &&
      !startGeocoding.loading &&
      startPosition
    ) {
      await startGeocoding.getLocationName(startPosition);
    }
  }, [startInfoWindowOpen, startGeocoding, startPosition]);

  // Handle end marker click - toggle InfoWindow
  const handleEndMarkerClick = useCallback(async () => {
    const newState = !endInfoWindowOpen;
    setEndInfoWindowOpen(newState);

    // Only fetch location if opening and not already loaded
    if (newState && !endGeocoding.locationName && !endGeocoding.loading) {
      await endGeocoding.getLocationName(currentEndPosition);
    }
  }, [endInfoWindowOpen, endGeocoding, currentEndPosition]);

  // Close InfoWindows
  const closeStartInfoWindow = useCallback(() => {
    setStartInfoWindowOpen(false);
  }, []);

  const closeEndInfoWindow = useCallback(() => {
    setEndInfoWindowOpen(false);
  }, []);

  // Body class management for custom info visibility
  useEffect(() => {
    if (endInfoWindowOpen || startInfoWindowOpen) {
      document.body.classList.add("custom-info-visible");
    } else {
      document.body.classList.remove("custom-info-visible");
    }
  }, [endInfoWindowOpen, startInfoWindowOpen]);

  // Trigger geocoding when end position changes and InfoWindow is open
  useEffect(() => {
    if (endInfoWindowOpen) {
      endGeocoding.getLocationName(currentEndPosition);
    }
  }, [currentEndPosition.lat, currentEndPosition.lng, endInfoWindowOpen]);

  // Load end marker location on component mount since it's open by default
  useEffect(() => {
    if (
      endInfoWindowOpen &&
      !endGeocoding.locationName &&
      !endGeocoding.loading
    ) {
      endGeocoding.getLocationName(currentEndPosition);
    }
  }, []);

  // Pan map to new position when end marker moves
  useEffect(() => {
    if (map && currentEndPosition) {
      map.panTo(currentEndPosition);
    }
  }, [currentEndPosition.lat, currentEndPosition.lng, map]);

  // Handle clicks outside the InfoWindow to close it
  useEffect(() => {
    if (!map) return;

    const handleMapClick = () => {
      setStartInfoWindowOpen(false);
      setEndInfoWindowOpen(false);
    };

    map.addListener("click", handleMapClick);

    return () => {
      google.maps.event.clearListeners(map, "click");
    };
  }, [map]);

  // Update polyline when points change - only create if we have 2+ different points
  useEffect(() => {
    if (!map || points.length < 2 || !shouldShowStartMarker) {
      // Clean up existing polyline if we don't have enough points or all points are the same
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
      return;
    }

    // Create polyline using Google Maps API
    const polyline = new google.maps.Polyline({
      path: points,
      geodesic: true,
      strokeColor: polylineColor,
      strokeOpacity: 0.8,
      strokeWeight: polylineWeight,
    });

    // Set polyline on map
    polyline.setMap(map);
    polylineRef.current = polyline;

    // Cleanup function
    return () => {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
    };
  }, [map, points, polylineWeight, polylineColor, shouldShowStartMarker]);

  return (
    <>
      {/* Start marker - only show if we should show start marker */}
      {shouldShowStartMarker && startPosition && (
        <AdvancedMarker
          ref={startMarkerRef}
          position={startPosition}
          title={startMarkerTitle}
          onClick={handleStartMarkerClick}
        >
          <Pin
            background={"var(--pin-background)"}
            borderColor={"var(--pin-border-color)"}
            glyphColor={"var(--pin-glyph-color)"}
          />
        </AdvancedMarker>
      )}

      {/* End marker - always show when we have at least one point */}
      <AdvancedMarker
        ref={endMarkerRef}
        position={currentEndPosition}
        title={shouldShowStartMarker ? endMarkerTitle : startMarkerTitle}
        onClick={handleEndMarkerClick}
      >
        <img src={mapMarkerImage} alt="End Marker" className="marker-icon" />
      </AdvancedMarker>

      {/* Start InfoWindow - only show if we should show start marker */}
      {shouldShowStartMarker && startPosition && (
        <LocationInfoWindow
          anchor={startMarker}
          isOpen={startInfoWindowOpen}
          onClose={closeStartInfoWindow}
          locationName={startGeocoding.locationName}
          loading={startGeocoding.loading}
          error={startGeocoding.error}
          timestamp={coordinates?.[0]?.timestamp?.toString()}
        />
      )}

      {/* End InfoWindow */}
      <LocationInfoWindow
        anchor={endMarker}
        isOpen={endInfoWindowOpen}
        onClose={closeEndInfoWindow}
        locationName={endGeocoding.locationName}
        loading={endGeocoding.loading}
        error={endGeocoding.error}
        timestamp={coordinates?.[
          coordinates?.length - 1
        ]?.timestamp?.toString()}
      />
    </>
  );
};

export default PolylineWithCarMarkers;
